/**
 * Logica pura delle cartelle documentali.
 *
 * Sta qui e non dentro il componente per la stessa ragione delle altre
 * `lib/`: e' l'unica parte del back-office che le verifiche sanno eseguire
 * (`verifiche/` compila moduli, non monta React), e ordinamento e destinazioni
 * ammesse sono esattamente cio' che sbagliato non si vede a occhio.
 *
 * Una cartella **non e' un nodo**: non ha un indirizzo pubblico, non compare
 * nella at_map e non entra nell'URL dei documenti che raggruppa. Qui dentro non
 * si costruisce nessun percorso pubblico, ed e' voluto.
 */

import type { Cartella } from "../api/types";

/** Separatore del percorso di cartelle, lo stesso dell'alberatura. */
export const SEPARATORE_CARTELLE = " / ";

/**
 * Ordina un gruppo di cartelle sorelle.
 *
 * Le annualita' vengono prima, **dalla piu' recente**; le altre per `ordine` e
 * poi per titolo. Un solo ordine alfabetico metterebbe gli anni in ordine
 * crescente, cioe' con il piu' vecchio in cima: l'opposto di come si consulta
 * un archivio. E' la stessa regola di `app/services/cartelle.py::ordina`, e le
 * due devono restare d'accordo — il cittadino e il redattore non possono vedere
 * la stessa ripartizione in due ordini diversi.
 */
export function ordinaCartelle(cartelle: readonly Cartella[]): Cartella[] {
  return [...cartelle].sort((a, b) => {
    if (a.e_annualita !== b.e_annualita) return a.e_annualita ? -1 : 1;
    if (a.e_annualita && b.e_annualita) return Number(b.titolo) - Number(a.titolo);
    if (a.ordine !== b.ordine) return a.ordine - b.ordine;
    return a.titolo.localeCompare(b.titolo, "it");
  });
}

/** Una cartella appiattita, con la sua profondita' nell'albero. */
export interface RigaCartella {
  cartella: Cartella;
  /** 1 per una cartella di primo livello. */
  profondita: number;
}

/**
 * Tutte le cartelle in ordine di visita, gia' ordinate a ogni livello.
 *
 * Serve agli elenchi a discesa («Sposta in…») e alla resa ad albero: entrambi
 * devono percorrere la gerarchia nello stesso ordine, altrimenti la cartella
 * scelta nel menu non e' quella che si vede in pagina.
 */
export function appiattisciCartelle(
  cartelle: readonly Cartella[],
  profondita = 1,
): RigaCartella[] {
  return ordinaCartelle(cartelle).flatMap((cartella) => [
    { cartella, profondita },
    ...appiattisciCartelle(cartella.figlie ?? [], profondita + 1),
  ]);
}

/** Cerca una cartella per identificativo in tutto l'albero. */
export function trovaCartella(
  cartelle: readonly Cartella[],
  id: number,
): Cartella | null {
  for (const { cartella } of appiattisciCartelle(cartelle)) {
    if (cartella.id === id) return cartella;
  }
  return null;
}

/**
 * Percorso leggibile di una cartella: «2026 / Allegati».
 *
 * E' testo per l'operatore, non un indirizzo: non finisce in alcun `href`.
 */
export function percorsoCartella(
  cartelle: readonly Cartella[],
  id: number | null,
): string {
  if (id == null) return "";
  const catena: string[] = [];
  let corrente = trovaCartella(cartelle, id);
  const visti = new Set<number>();
  while (corrente && !visti.has(corrente.id)) {
    visti.add(corrente.id);
    catena.unshift(corrente.titolo);
    corrente = corrente.id_padre == null ? null : trovaCartella(cartelle, corrente.id_padre);
  }
  return catena.join(SEPARATORE_CARTELLE);
}

/** Identificativi di tutte le discendenti di una cartella. */
export function discendentiDiCartella(cartella: Cartella): number[] {
  return (cartella.figlie ?? []).flatMap((figlia) => [
    figlia.id,
    ...discendentiDiCartella(figlia),
  ]);
}

/**
 * Destinazioni in cui `daSpostare` puo' essere collocata.
 *
 * Esclude se' stessa e le proprie discendenti: spostare una cartella dentro un
 * proprio ramo staccherebbe l'intero sottoalbero senza cancellarlo — sparirebbe
 * dalla pagina restando nel database. L'API lo rifiuta con
 * `CARTELLA_CICLO`; qui si evita di proporre la scelta, perche' un menu che
 * offre un'opzione destinata a fallire e' un menu che mente.
 *
 * `daSpostare` a `null` significa «sto creando una cartella nuova»: allora ogni
 * cartella esistente e' una destinazione ammessa.
 */
export function destinazioniAmmesse(
  cartelle: readonly Cartella[],
  daSpostare: Cartella | null,
): RigaCartella[] {
  if (!daSpostare) return appiattisciCartelle(cartelle);
  const vietati = new Set<number>([daSpostare.id, ...discendentiDiCartella(daSpostare)]);
  return appiattisciCartelle(cartelle).filter((riga) => !vietati.has(riga.cartella.id));
}

/**
 * Profondita' massima raggiunta dall'albero delle cartelle.
 *
 * Il tetto vero lo impone l'API (`PROFONDITA_MASSIMA_CARTELLE`); questo serve a
 * dirlo all'operatore **prima** che una creazione fallisca.
 */
export function profonditaMassima(cartelle: readonly Cartella[]): number {
  return appiattisciCartelle(cartelle).reduce(
    (massimo, riga) => Math.max(massimo, riga.profondita),
    0,
  );
}

/** Numero complessivo di cartelle nell'albero. */
export function conteggioCartelle(cartelle: readonly Cartella[]): number {
  return appiattisciCartelle(cartelle).length;
}
